import { inject, Injectable } from '@angular/core';
import {
  deleteEntitiesByPredicate,
  selectAllEntities,
  selectAllEntitiesApply,
  selectEntity,
  setEntities,
  upsertEntities,
  upsertEntitiesById,
} from '@ngneat/elf-entities';
import { ContentParser } from '@thiememeulenhoff/js-sdk';
import { ContentService } from '@thiememeulenhoff/ng-api-gateway';
import { translate } from '@thiememeulenhoff/translations';
import {
  Arrangement,
  ArrangementResource,
  ArrangementType,
  Cluster,
  ClusterWithArrangements,
  ContentItem,
  Method,
  RecentScoreItem,
  Resource,
  ResourceIdentifierWithType,
  ResourceType,
} from '@thiememeulenhoff/types';
import { combineLatest, filter, map, of, switchMap, take, tap } from 'rxjs';
import * as stores from './content.store';

@Injectable({
  providedIn: 'root',
})
export class ContentFacade {
  readonly #resources = stores.resources;

  readonly #content = stores.content;

  readonly #methods = stores.methods;

  readonly #arrangements = stores.arrangements;

  readonly #clusters = stores.clusters;

  readonly #clustersWithArrangements = stores.clustersWithArrangements;

  readonly #arrangementResource = stores.arrangementResourceByCode;

  readonly #arrangementResources = stores.arrangementResources;

  readonly #referencesResources = stores.referencesResources;

  readonly #contentService = inject(ContentService);

  #contentParser?: ContentParser;

  readonly methods$ = this.#methods.pipe(selectAllEntities());

  #methodsFetched = false;

  readonly arrangement$ = (code: string) =>
    this.#arrangements.pipe(selectEntity(code));

  readonly arrangements$ = this.#arrangements.pipe(selectAllEntities());

  readonly clusters$ = this.#clusters.pipe(selectAllEntities());

  readonly clustersWithArrangements$ =
    this.#clustersWithArrangements.pipe(selectAllEntities());

  readonly arrangementResourceByCode$ = (arrangementCode: string) => {
    return this.#arrangementResource.pipe(selectEntity(arrangementCode));
  };

  readonly arrangementResourceById$ = (contentIdentifier: string) => {
    return this.#arrangementResources.pipe(selectEntity(contentIdentifier));
  };

  readonly resourceById$ = (contentIdentifier: string) =>
    this.#resources.pipe(selectEntity(contentIdentifier));

  readonly referencesResourcesById$ = (contentIdentifier: string) =>
    this.#referencesResources.pipe(selectEntity(contentIdentifier));

  readonly contentById$ = (contentIdentifier: string) =>
    this.#content.pipe(selectEntity(contentIdentifier));

  readonly fetchMethodsIfNotFetched = () => {
    if (!this.#methodsFetched) {
      return this.fetchMethods();
    }

    return of();
  };

  readonly fetchMethods = () => {
    this.#methodsFetched = true;
    return this.#contentService.fetchMethods().pipe(tap(this.#upsertMethods));
  };

  readonly methodBySlug$ = (slug: string) =>
    this.#methods.pipe(selectEntity(slug));

  readonly questionsByParentId$ = (contentIdentifier: string) =>
    this.#resources.pipe(
      selectAllEntitiesApply({
        filterEntity: (e) =>
          e.parentIdentifier === contentIdentifier &&
          e.digitalResourceType === 'question',
      }),
    );

  readonly clustersWithArrangementsByMethodSlug$ = (methodSlug: string) =>
    this.#clustersWithArrangements.pipe(
      selectAllEntitiesApply({
        filterEntity: (e) => e.methodSlug === methodSlug,
      }),
    );

  readonly methodClusterArrangementsBySlug$ = (methodSlug: string) =>
    combineLatest([
      this.methodBySlug$(methodSlug),
      this.clustersWithArrangementsByMethodSlug$(methodSlug),
    ]).pipe(
      filter(
        ([method, clusterArrangements]) => !!method && !!clusterArrangements,
      ),
      map(([method, clusterArrangements]) => {
        return {
          method: method as Method,
          clusterArrangements,
        };
      }),
      filter(({ clusterArrangements }) => clusterArrangements.length > 0),
    );

  enableLocalParsing() {
    this.#contentParser = new ContentParser('assets/content/local-index.xsl');
  }

  fetchArrangementResource(
    arrangementCode: string,
    depth = 4,
    includeTypes: Array<ResourceType> = [],
  ) {
    return this.#contentService
      .fetchResourceByArrangementCode<ArrangementResource>(
        arrangementCode,
        '',
        depth,
        {
          include: [
            'thedContentIdentifier',
            'alexContentIdentifier',
            'streamMetadata.method',
            'navigationMetadata.audience',
            'navigationMetadata.backgroundImage',
            'navigationMetadata.backgroundImageCopyright',
            'navigationMetadata.hasTerms',
            'navigationMetadata.hasSources',
            'navigationMetadata.hasResultables',
            'navigationMetadata.links',
            'navigationMetadata.contentType',
            'learningMetadata.language',
            'learningMetadata.typicalLearningTime',
            'instructorAssets',
          ],
        },
        {
          include: ['module', 'chapter', 'paragraph', ...includeTypes],
        },
      )
      .pipe(tap(this.#upsertArrangementResources));
  }

  fetchArrangementResourceIfMissing(
    arrangementCode: string,
    depth = 4,
    includeTypes: Array<ResourceType> = [],
  ) {
    return this.arrangementResourceByCode$(arrangementCode).pipe(
      take(1),
      switchMap((arrangementResource) => {
        if (arrangementResource) {
          return of(arrangementResource);
        }

        return this.fetchArrangementResource(
          arrangementCode,
          depth,
          includeTypes,
        );
      }),
    );
  }

  fetchResources(
    arrangementCode: string,
    contentIdentifier: string,
    depth = 1,
    disabled = true,
  ) {
    return this.#contentService
      .fetchResourceByArrangementCode<Resource>(
        arrangementCode,
        contentIdentifier,
        depth,
        {
          include: [
            'thedContentIdentifier',
            'alexContentIdentifier',
            'terms',
            'streamMetadata.method',
            'navigationMetadata.scoreType',
            'navigationMetadata.questionType',
            'navigationMetadata.hasTheory',
            'navigationMetadata.hasTerms',
            'navigationMetadata.hasSources',
            'navigationMetadata.hasResultables',
            'navigationMetadata.backgroundImage',
            'navigationMetadata.backgroundImageCopyright',
            'navigationMetadata.audience',
            'navigationMetadata.links',
            'navigationMetadata.contentType',
            'editionMetadata.glueContent',
            'editionMetadata.assessment',
            'learningMetadata.language',
            'learningMetadata.isAdaptive',
            'learningMetadata.typicalLearningTime',
            'smartMetadata.allLearningGoals',
            'instructorAssets',
          ],
        },
        undefined,
        disabled,
      )
      .pipe(tap(this.#upsertResources));
  }

  fetchReferenceResources(
    arrangementCode: string,
    contentIdentifier = '',
    depth = 6,
  ) {
    return this.#contentService
      .fetchResourceByArrangementCode<Resource>(
        arrangementCode,
        contentIdentifier,
        depth,
        {
          include: [
            'thedContentIdentifier',
            'alexContentIdentifier',
            'terms',
            'navigationMetadata.backgroundImage',
            'navigationMetadata.backgroundImageCopyright',
            'navigationMetadata.audience',
            'navigationMetadata.hasSources',
            'navigationMetadata.hasTerms',
            'learningMetadata.language',
          ],
        },
        {
          include: [
            'chapter',
            'itemMap',
            'module',
            'paragraph',
            'sourceMap',
            'source',
          ],
        },
      )
      .pipe(tap(this.#upsertReferencesResources));
  }

  fetchContent(
    arrangementCode: string,
    contentIdentifier: string,
    includeSourceMaterial = false,
  ) {
    const parser = this.#contentParser;
    const { fetchContentById, fetchRawContentById } = this.#contentService;
    const fetch = parser ? fetchRawContentById : fetchContentById;

    return fetch(
      arrangementCode,
      contentIdentifier,
      includeSourceMaterial,
    ).pipe(
      map((content) => parser?.parseFromString(content) || content),
      tap((content) => this.#upsertContent(contentIdentifier, content)),
    );
  }

  fetchSourceContent(arrangementCode: string, contentIdentifier: string) {
    const parser = this.#contentParser;
    const { fetchContentById, fetchRawContentById } = this.#contentService;
    const fetch = parser ? fetchRawContentById : fetchContentById;

    return fetch(arrangementCode, contentIdentifier, true).pipe(
      map((content) => parser?.parseFromString(content) || content),
    );
  }

  fetchRawContent(arrangementCode: string, contentIdentifier: string) {
    return this.#contentService
      .fetchRawContentById(arrangementCode, contentIdentifier, true)
      .pipe(map((content) => content));
  }

  fetchMethod(methodSlug: string) {
    return this.#contentService
      .fetchMethodBySlug(methodSlug)
      .pipe(tap((method) => this.#upsertMethods([method])));
  }

  fetchClusters() {
    return this.#contentService
      .fetchClusters()
      .pipe(tap((clusters) => this.#setClusters(clusters)));
  }

  fetchArrangement(code: string) {
    return this.#contentService
      .fetchArrangementByCode(code)
      .pipe(tap((arrangement) => this.#upsertArrangements([arrangement])));
  }

  fetchArrangementsByType(type: ArrangementType) {
    return this.#contentService
      .fetchPublishedArrangementsOfType(type)
      .pipe(
        tap((arrangements) => this.#upsertArrangements(arrangements, type)),
      );
  }

  fetchResourcesForMostRecentProgress(recentScores?: Array<RecentScoreItem>) {
    if (!recentScores || recentScores.length === 0) {
      return of([]);
    }

    if (recentScores.length === 1 && recentScores[0] === null) {
      return of([]);
    }

    let recentScoreItems = recentScores;
    if (!Array.isArray(recentScores)) {
      recentScoreItems = [recentScores];
    }

    const fields = {
      include: [
        'thedContentIdentifier',
        'alexContentIdentifier',
        'streamMetadata.method',
        'navigationMetadata.backgroundImage',
        'navigationMetadata.backgroundImageCopyright',
      ],
    };

    return this.#contentService.fetchMultipleResources(
      recentScoreItems.map((score) => ({
        arrangementCode: score.arrangementCode,
        contentIdentifier: score.contentIdentifier,
      })),
      fields,
    );
  }

  // #region Store mutations
  #upsertArrangementResources = (resources: Array<ArrangementResource>) => {
    this.#arrangementResource.update(
      upsertEntities(
        resources.map(
          (resource) =>
            ({
              ...resource,
              code: resource.streamMetadata?.code,
            }) as ArrangementResource,
        ),
      ),
    );
    this.#arrangementResources.update(upsertEntities(resources));

    const entities = ContentFacade.flattenResources(resources);
    this.#arrangementResources.update(upsertEntities(entities));
  };

  #upsertReferencesResources = (resources: Array<Resource>) => {
    this.#referencesResources.update(upsertEntities(resources));
  };

  #upsertResources = (resources: Array<Resource>) => {
    const entities = ContentFacade.flattenResources(resources);

    entities.forEach((entity) => {
      const parentIdentifier =
        entity.parentIdentifier || entity.parent?.contentIdentifier;

      this.#resources.update(
        upsertEntitiesById(entity.contentIdentifier, {
          updater: () => ({
            ...entity,
            parentIdentifier,
          }),
          creator: () => ({ ...entity, parentIdentifier }),
          mergeUpdaterWithCreator: false,
        }),
      );
    });
  };

  #upsertContent = (contentIdentifier: string, content: string) => {
    this.#content.update(upsertEntities([{ contentIdentifier, content }]));
  };

  #setClusters = (clusters: Array<Cluster>) => {
    this.#clusters.update(setEntities(clusters));
    this.#setClustersWithArrangements().subscribe();
  };

  #upsertArrangements = (
    arrangements: Array<Arrangement>,
    type?: ArrangementType,
  ) => {
    if (type) {
      this.#arrangements.update(
        deleteEntitiesByPredicate((arrangement) => arrangement.type === type),
      );
    }

    this.#arrangements.update(upsertEntities(arrangements));
    this.#setClustersWithArrangements().subscribe();
  };

  #setClustersWithArrangements = () =>
    combineLatest([this.clusters$, this.arrangements$]).pipe(
      map(([clusters, arrangements]) => {
        const userDefinedArrangements: Array<Arrangement> = [];

        const arrangementsMap = arrangements.reduce(
          (acc, arrangement) => {
            if (arrangement.type === 'editorial') {
              acc[arrangement.code] = arrangement;
            } else {
              userDefinedArrangements.push(arrangement);
            }

            return acc;
          },
          {} as Record<string, Arrangement>,
        );

        const mapped = clusters.map((cluster) => {
          const arrangements = cluster.arrangementCodes
            .filter((code) => !!arrangementsMap[code])
            .map((code) => arrangementsMap[code]);

          return { ...cluster, arrangements } as ClusterWithArrangements;
        });

        const userDefinedArrangementsMap = userDefinedArrangements.reduce(
          (acc, arrangement) => {
            const methodSlug = arrangement.methodObject.slug;
            (acc[methodSlug] = acc[methodSlug] ?? []).push(arrangement);

            return acc;
          },
          {} as Record<string, Array<Arrangement>>,
        );

        Object.entries(userDefinedArrangementsMap).forEach(
          ([methodSlug, userDefinedArrangements]) => {
            mapped.unshift({
              code: `user-defined-${methodSlug}`,
              name: translate(
                'onboarding.preference.custom_arrangements.title',
              ),
              methodName: userDefinedArrangements[0].methodObject.name,
              methodSlug: userDefinedArrangements[0].methodObject.slug,
              arrangementCodes: userDefinedArrangements.map(
                (a: Arrangement) => a.code,
              ),
              arrangements: userDefinedArrangements,
            });
          },
        );

        return mapped;
      }),
      tap((clusters) =>
        this.#clustersWithArrangements.update(setEntities(clusters)),
      ),
    );

  #upsertMethods = (methods: Array<Method>) => {
    this.#methods.update(upsertEntities(methods));
  };
  // #endregion

  static flattenResources<T extends ArrangementResource | Resource>(
    resources: Array<T>,
    parents: Array<ResourceIdentifierWithType> = [],
  ): Array<T> {
    const entities: Array<T> = [];

    resources.forEach((resource) => {
      const parentIdentifier =
        parents.length > 0
          ? parents[parents.length - 1].contentIdentifier
          : undefined;

      const children = resource.children || [];
      const copy = { ...resource, parentIdentifier, parents } as T;
      entities.push(
        copy,
        ...ContentFacade.flattenResources(children as Array<T>, [
          ...parents,
          {
            contentIdentifier: resource.contentIdentifier,
            digitalResourceType: resource.digitalResourceType,
          },
        ]),
      );
    });

    return entities;
  }

  fetchContentByContentPath(
    streamCode: string,
    contentPath: string,
    depth?: number,
    digitalResourceTypes?: Array<string>,
  ) {
    return this.#contentService
      .fetchByContentPath(streamCode, contentPath, depth, digitalResourceTypes)
      .pipe(
        tap((items: Array<ContentItem>) => {
          items.forEach((item) =>
            this.#upsertContent(item.identifier, item.content),
          );
        }),
      );
  }
}
